import {useQuery} from '@tanstack/react-query';

import {getTestingApiClient} from '../client.ts';
import {testingApiConfig} from '../config/index.ts';

// FNP-995. The handler registry, read from the deployed testing-api.
//
// `listRegisteredActions` answers what this deployment can actually PERFORM,
// as opposed to what the Action catalog claims. Until it existed the console
// had no way to ask, so `actionCode` was a required free-text box and a human
// was handed six strings to type with warnings about typos.
//
// ⚠️ THIS IS SUGGESTIONS, NEVER A WHITELIST. FNP-859's rule stands: an
// unregistered code is LEGAL. The executor fails open and records an
// `unimplemented` observation, which is what lets a test be authored BEFORE its
// handler exists — a demand signal sitting in the data. Anything built on this
// hook must keep an arbitrary code saveable.
//
// ⚠️ `dependsOn` is CODE-OWNED and read-only. run-wake.ts interprets it to
// drive skip-vs-attempt and cascade attribution, so it is displayed and never
// edited.

export const registeredActionsKeys = {
    all: ['testing-api', 'registered-actions'] as const,
};

export type RegisteredAction = {
    actionCode: string,
    dependsOn: string[],
};

type ListRegisteredActions = {
    listRegisteredActions: (RegisteredAction | null)[] | null,
};

const LIST_REGISTERED_ACTIONS = `
query ListRegisteredActions {
  listRegisteredActions {
    actionCode
    dependsOn
  }
}`;

// The testing-api is not deployed to every stage the console runs in — there is
// no prod endpoint, by decision. Asking anyway would throw out of
// getTestingApiClient() on every render in prod, so the query is disabled and
// the caller sees an empty registry rather than an error.
const testingApiAvailable = Boolean(testingApiConfig);

export const useRegisteredActionsQuery = () => {
    return useQuery({
        queryKey: registeredActionsKeys.all,
        enabled: testingApiAvailable,
        queryFn: async (): Promise<RegisteredAction[]> => {
            const client = getTestingApiClient();
            const response = await client.graphql<ListRegisteredActions>({
                query: LIST_REGISTERED_ACTIONS,
            });

            const items: (RegisteredAction | null)[] =
                ('data' in response ? response.data?.listRegisteredActions : null) ?? [];

            return items
                .filter((item): item is RegisteredAction => item !== null)
                .map((item: RegisteredAction) => ({
                    actionCode: item.actionCode,
                    dependsOn: item.dependsOn ?? [],
                }));
        },
        // The registry only changes when the canary bundle is redeployed, so
        // this is stable for the length of any authoring session.
        staleTime: 5 * 60 * 1000,
    });
};

export type RegisteredActionOption = {
    value: string,
    label: string,
    dependsOn: string[],
};

const labelFor = (action: RegisteredAction): string => {
    return action.dependsOn.length > 0
        ? `${action.actionCode} (needs ${action.dependsOn.join(', ')})`
        : action.actionCode;
};

/**
 * Options for the actionCode picker, plus whether a given code is registered.
 *
 * ⚠️ `isUnregistered` is a WARNING signal, never a validation failure. It is
 * false while the registry is still loading or unavailable, so a slow query
 * cannot make a legitimate code flash as unknown.
 */
export const useRegisteredActionOptions = (currentCode?: string | null) => {
    const {
        data,
        isLoading,
        isError,
    } = useRegisteredActionsQuery();

    const actions = data ?? [];

    const options: RegisteredActionOption[] = actions.map(action => ({
        value: action.actionCode,
        label: labelFor(action),
        dependsOn: action.dependsOn,
    }));

    const code = currentCode?.trim();

    const registryUsable = testingApiAvailable && !isLoading && !isError && actions.length > 0;

    const isUnregistered = Boolean(
        code && registryUsable && !actions.some(action => action.actionCode === code),
    );

    const selectedAction = code
        ? actions.find(action => action.actionCode === code)
        : undefined;

    return {
        options,
        actions,
        selectedAction,
        isUnregistered,
        isLoading,
        isError,
        registryAvailable: testingApiAvailable,
    };
};
